import apiClient from './client';
import type { Topic, CohortSessionTopic } from '../../types';

export interface CreateTopicResult {
  topic: Topic;
  // True when a brand-new row was inserted; false when an existing topic was
  // returned because the entered name matched (case-insensitively) an existing
  // topic. Drives the choice between "Topic created" and "Topic already exists
  // — selected" toasts (MED-832 AC7).
  created: boolean;
}

/**
 * API for managing topics and cohort session topic assignments
 */
export const topicsApi = {
  /**
   * Get all active topics
   */
  getAll: async (): Promise<Topic[]> => {
    return apiClient.get<Topic[]>('/api/topics');
  },

  /**
   * Get a single topic by ID
   */
  getById: async (id: string): Promise<Topic> => {
    return apiClient.get<Topic>(`/api/topics/${id}`);
  },

  /**
   * Create a new topic (or return existing on case-insensitive name match).
   * MED-832 AC5/AC7: idempotent — duplicate name returns the existing topic.
   */
  create: async (name: string): Promise<CreateTopicResult> => {
    const result = await apiClient.rawPostWithMeta<Topic>('/api/topics', {
      name: name.trim(),
    });
    return {
      topic: result.data,
      created: Boolean(result.meta?.created),
    };
  },

  /**
   * Get session topic assignments for a cohort
   */
  getCohortSessionTopics: async (cohortId: string): Promise<CohortSessionTopic[]> => {
    return apiClient.get<CohortSessionTopic[]>(`/api/cohorts/${cohortId}/session-topics`);
  },

  /**
   * Assign a topic to a session number in a cohort.
   *
   * MED-832: optional displayName overrides the participant-facing label for
   * this cohort/session-number assignment. Empty string normalizes to NULL on
   * the server, so passing '' clears any prior override (AC14).
   */
  assignSessionTopic: async (
    cohortId: string,
    sessionNumber: number,
    topicId: string,
    displayName?: string | null,
  ): Promise<CohortSessionTopic> => {
    const body: { topic_id: string; display_name?: string | null } = { topic_id: topicId };
    if (displayName !== undefined) {
      body.display_name = displayName;
    }
    return apiClient.put<CohortSessionTopic>(
      `/api/cohorts/${cohortId}/session-topics/${sessionNumber}`,
      body,
    );
  },

  /**
   * Remove topic assignment from a session number
   */
  removeSessionTopic: async (cohortId: string, sessionNumber: number): Promise<void> => {
    return apiClient.delete<void>(`/api/cohorts/${cohortId}/session-topics/${sessionNumber}`);
  },
};

export default topicsApi;
